/**
 * marketsService.js — API service for Global Markets endpoints.
 * Uses the MSAL-authenticated API client.
 */

import { api } from '../auth/apiClient';

/** GET /api/markets/config */
export async function getMarketsConfig() {
    return api.get('/markets/config');
}

/** GET /api/markets/quotes?group={us|ca|commodities} */
export async function getMarketsQuotes(group) {
    return api.get(`/markets/quotes?group=${encodeURIComponent(group)}`);
}

/** GET /api/markets/history?symbol=...&period=... */
export async function getMarketsHistory(symbol, period = '1Y', options = {}) {
    const params = new URLSearchParams();
    params.set('symbol', symbol);
    params.set('period', period);
    return api.get(`/markets/history?${params.toString()}`, {
        timeout: 120000,
        signal: options?.signal,
    });
}

/** GET /api/markets/fundamentals?symbol=... */
export async function getMarketsFundamentals(symbol) {
    return api.get(`/markets/fundamentals?symbol=${encodeURIComponent(symbol)}`);
}

/** GET /api/markets/analytics?symbol=...&period=... */
export async function getMarketsAnalytics(symbol, period = 'MAX') {
    const params = new URLSearchParams();
    params.set('symbol', symbol);
    params.set('period', period);
    return api.get(`/markets/analytics?${params.toString()}`, {
        timeout: 120000,
    });
}

// ─────── Valuation Series & Snapshot ───────

/** GET /api/markets/valuation/snapshot?symbol=... */
export async function getValuationSnapshot(symbol) {
    return api.get(`/markets/valuation/snapshot?symbol=${encodeURIComponent(symbol)}`);
}

/** GET /api/markets/valuation/series?symbol=...&years=10 */
export async function getValuationSeries(symbol, years = 10) {
    const params = new URLSearchParams();
    params.set('symbol', symbol);
    params.set('years', String(years));
    return api.get(`/markets/valuation/series?${params.toString()}`, {
        timeout: 120000,
    });
}

// ─────── Alerts CRUD ───────

/** GET /api/markets/alerts[?symbol=...] */
export async function getMarketAlerts(symbol) {
    const url = symbol
        ? `/markets/alerts?symbol=${encodeURIComponent(symbol)}`
        : '/markets/alerts';
    return api.get(url);
}

/** POST /api/markets/alerts */
export async function createMarketAlert({ symbol, metric, condition, threshold }) {
    return api.post('/markets/alerts', { symbol, metric, condition, threshold });
}

/** PUT /api/markets/alerts/:id */
export async function updateMarketAlert(alertId, updates) {
    return api.put(`/markets/alerts/${alertId}`, updates);
}

/** DELETE /api/markets/alerts/:id */
export async function deleteMarketAlert(alertId) {
    return api.delete(`/markets/alerts/${alertId}`);
}

// ─────── Notifications ───────

/** GET /api/markets/notifications[?unreadOnly=true&limit=50] */
export async function getNotifications({ unreadOnly = false, limit = 50 } = {}) {
    const params = new URLSearchParams();
    if (unreadOnly) params.set('unreadOnly', 'true');
    params.set('limit', String(limit));
    return api.get(`/markets/notifications?${params.toString()}`);
}

/** GET /api/markets/notifications/count */
export async function getUnreadNotificationCount() {
    return api.get('/markets/notifications/count');
}

/** POST /api/markets/notifications/read — mark one read */
export async function markNotificationRead(notificationId) {
    return api.post('/markets/notifications/read', { notificationId });
}

/** POST /api/markets/notifications/readAll — mark all read */
export async function markAllNotificationsRead() {
    return api.post('/markets/notifications/readAll', {});
}

// ─────── Provider Status ───────

/** GET /api/markets/providers — diagnostics for configured data providers */
export async function getProviderStatus() {
    return api.get('/markets/providers');
}
